import { Form } from "@unform/web";
import React, { useRef, useState, useEffect } from 'react';
import { AiOutlineSearch } from "react-icons/ai";
import { toast } from "react-toastify";
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Yup from "yup";
import { Line, Bar } from 'react-chartjs-2';
import DivDiagonal from "../../components/DivDiagonal";
import CustomButton from "../../components/Form/Button";
import InputDate from "../../components/Form/InputDate";
import messageTypes from "../../constants/MessagesTypes";
import { getSummary, getSalesReport, getTopProducts } from "../../services/Dashboard";
import {
  todayInputDate,
  firstDayOfCurrentMonthInputDate,
  firstDayOfCurrentYearInputDate,
  currentWeekStartInputDate
} from "../../utils/date";
import {
  DashboardWrapper,
  PageHeader,
  FilterCard,
  FilterTitle,
  ShortcutRow,
  ShortcutBtn,
  FormWrapper,
  StyledFilterFormWrapper,
  ContentArea,
  StatsGrid,
  StatCard,
  ChartsGrid,
  ChartCard,
  ChartTitle,
  TableCard,
  TableTitle,
  StyledTableContainer,
  ProductsTable,
  EmptyState
} from "./styles";

const SHORTCUTS = [
  {
    label: 'Hoje',
    getRange: () => ({ startDate: todayInputDate(), endDate: todayInputDate() }),
  },
  {
    label: 'Esta semana',
    getRange: () => ({ startDate: currentWeekStartInputDate(), endDate: todayInputDate() }),
  },
  {
    label: 'Este mês',
    getRange: () => ({ startDate: firstDayOfCurrentMonthInputDate(), endDate: todayInputDate() }),
  },
  {
    label: 'Este ano',
    getRange: () => ({ startDate: firstDayOfCurrentYearInputDate(), endDate: todayInputDate() }),
  },
];

const formatCurrency = (value) => {
  if (!value && value !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

export default function Dashboard() {
  const formRef = useRef(null);
  const history = useHistory();
  const { isOperator, currentCompany } = useSelector((state) => state.CompanyReducer);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [salesReport, setSalesReport] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [filter, setFilter] = useState({
    startDate: null,
    endDate: null,
  });
  const [activeShortcut, setActiveShortcut] = useState(null);

  useEffect(() => {
    // Redirecionar FUNCIONÁRIO para página de vendas
    if (isOperator) {
      toast.info('Funcionários não têm acesso ao dashboard financeiro.');
      history.push('/sales');
      return;
    }
  }, [isOperator, history]);

  useEffect(() => {
    loadDashboardData();
  }, [filter, currentCompany?.id]);

  async function loadDashboardData() {
    setLoading(true);
    try {
      const [summaryRes, salesRes, productsRes] = await Promise.all([
        getSummary(filter.startDate, filter.endDate),
        getSalesReport(filter.startDate, filter.endDate),
        getTopProducts(10, filter.startDate, filter.endDate)
      ]);

      if (summaryRes && summaryRes.data) {
        setSummary(summaryRes.data);
      }
      if (salesRes && salesRes.data) {
        setSalesReport(salesRes.data);
      }
      if (productsRes && productsRes.data && productsRes.data.data) {
        setTopProducts(productsRes.data.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados do dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(data) {
    try {
      formRef.current.setErrors({});
      const schema = Yup.object().shape({});

      await schema.validate(data, {
        abortEarly: false,
      });

      setActiveShortcut(null);
      setFilter({
        startDate: data.startDate,
        endDate: data.endDate,
      });

    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors = {};
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        formRef.current.setErrors(validationErrors);
        toast.warning(messageTypes.REQUIRED_FIELDS);
        return;
      }

      toast.error(messageTypes.ERROR);
    }
  }

  function handleShortcut(label, startDate, endDate) {
    setActiveShortcut(label);
    setFilter({ startDate, endDate });
  }

  const salesChartData = salesReport?.dailySales ? {
    labels: salesReport.dailySales.map(item => {
      const date = new Date(item.saleDay);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }),
    datasets: [
      {
        label: 'Receita (R$)',
        data: salesReport.dailySales.map(item => item.revenue),
        borderColor: '#f29205',
        backgroundColor: 'rgba(242, 146, 5, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#f29205',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
      }
    ]
  } : null;

  const salesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: {
          color: '#e0e0e0',
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#f29205',
        bodyColor: '#e0e0e0',
        borderColor: '#f29205',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            return `Receita: ${formatCurrency(context.parsed.y)}`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#b0b0b0'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      y: {
        ticks: {
          color: '#b0b0b0',
          callback: function(value) {
            return formatCurrency(value);
          }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      }
    }
  };

  const productsChartData = topProducts.length > 0 ? {
    labels: topProducts.slice(0, 5).map(p => p.name.length > 20 ? p.name.substring(0, 20) + '...' : p.name),
    datasets: [
      {
        label: 'Quantidade Vendida',
        data: topProducts.slice(0, 5).map(p => p.totalQuantity),
        backgroundColor: 'rgba(242, 146, 5, 0.8)',
        borderColor: '#f29205',
        borderWidth: 2,
      }
    ]
  } : null;

  const productsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      labels: {
        fontColor: '#e0e0e0',
        fontSize: window.innerWidth < 768 ? 10 : 12,
        boxWidth: window.innerWidth < 768 ? 10 : 12
      }
    },
    tooltips: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleFontColor: '#f29205',
      bodyFontColor: '#e0e0e0',
      borderColor: '#f29205',
      borderWidth: 1,
      titleFontSize: window.innerWidth < 768 ? 12 : 14,
      bodyFontSize: window.innerWidth < 768 ? 11 : 12
    },
    scales: {
      xAxes: [{
        ticks: {
          fontColor: '#b0b0b0',
          fontSize: window.innerWidth < 768 ? 10 : 12,
          maxRotation: window.innerWidth < 768 ? 45 : 0,
          minRotation: window.innerWidth < 768 ? 45 : 0
        },
        gridLines: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      }],
      yAxes: [{
        ticks: {
          fontColor: '#b0b0b0',
          beginAtZero: true,
          fontSize: window.innerWidth < 768 ? 10 : 12
        },
        gridLines: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      }]
    }
  };

  return (
    <DashboardWrapper>
      <DivDiagonal/>
      <ContentArea>
        <PageHeader>
          <h1>
            <i className="fas fa-fw fa-tachometer-alt"/> Painel
          </h1>
        </PageHeader>
        
        <FilterCard>
          <StyledFilterFormWrapper>
            <Form ref={formRef} onSubmit={handleSubmit}>
              <FilterTitle>
                <i className="fas fa-funnel-dollar"/> Filtro
              </FilterTitle>

              <div className="row">
                <div className="col-12">
                  <ShortcutRow>
                    {SHORTCUTS.map(({ label, getRange }) => (
                      <ShortcutBtn
                        key={label}
                        type="button"
                        $active={activeShortcut === label}
                        aria-pressed={activeShortcut === label}
                        onClick={() => {
                          const { startDate, endDate } = getRange();
                          handleShortcut(label, startDate, endDate);
                        }}
                      >
                        {label}
                      </ShortcutBtn>
                    ))}
                  </ShortcutRow>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4 col-xs-12 mb-3">
                  <InputDate
                    key={`start-${filter.startDate}`}
                    name="startDate"
                    label={'Data inicial'}
                    value={filter.startDate}
                  />
                </div>
                <div className="col-md-4 col-xs-12 mb-3">
                  <InputDate
                    key={`end-${filter.endDate}`}
                    name="endDate"
                    label={'Data final'}
                    value={filter.endDate}
                  />
                </div>
                <div className="col-md-4 col-xs-12 d-flex align-items-end mb-3">
                  <CustomButton
                    className="btn btn-primary font-weight-bold text-capitalize mobile-button"
                    type="submit"
                    startIcon={<AiOutlineSearch/>}
                    style={{
                      background: 'linear-gradient(135deg, #f29205 0%, #ff6b35 100%)',
                      border: 'none',
                      color: '#ffffff',
                      padding: '0 32px',
                      borderRadius: '10px',
                      fontWeight: 600,
                      boxShadow: '0 4px 15px rgba(242, 146, 5, 0.3)',
                      transition: 'all 0.3s ease',
                      width: 'auto',
                      minWidth: 'auto',
                      height: '56px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(242, 146, 5, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(242, 146, 5, 0.3)';
                    }}
                  >
                    Buscar
                  </CustomButton>
                </div>
              </div>
            </Form>
          </StyledFilterFormWrapper>
        </FilterCard>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#b0b0b0' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '48px', color: '#f29205' }}></i>
            <p style={{ marginTop: '16px' }}>Carregando dados...</p>
          </div>
        ) : (
          <>
            {/* Cards de Resumo Executivo */}
            {summary && (
              <StatsGrid>
                <StatCard>
                  <i className="fas fa-shopping-cart stat-icon"></i>
                  <div className="stat-value">{summary.salesToday || 0}</div>
                  <div className="stat-label">Vendas Hoje</div>
                </StatCard>
                <StatCard>
                  <i className="fas fa-dollar-sign stat-icon"></i>
                  <div className="stat-value">{formatCurrency(summary.revenueToday || 0)}</div>
                  <div className="stat-label">Receita Hoje</div>
                </StatCard>
                <StatCard>
                  <i className="fas fa-calendar-alt stat-icon"></i>
                  <div className="stat-value">{formatCurrency(summary.revenueMonth || 0)}</div>
                  <div className="stat-label">Receita do Mês</div>
                  {summary.growthPercentage !== undefined && (
                    <div className={`stat-change ${summary.growthPercentage >= 0 ? 'positive' : 'negative'}`}>
                      <i className={`fas fa-arrow-${summary.growthPercentage >= 0 ? 'up' : 'down'}`}></i>
                      {Math.abs(summary.growthPercentage).toFixed(1)}% vs mês anterior
                    </div>
                  )}
                </StatCard>
                <StatCard>
                  <i className="fas fa-box stat-icon"></i>
                  <div className="stat-value">{summary.itemsSoldToday || 0}</div>
                  <div className="stat-label">Itens Vendidos Hoje</div>
                </StatCard>
              </StatsGrid>
            )}

            {/* Gráficos */}
            <ChartsGrid>
              {salesChartData && (
                <ChartCard>
                  <ChartTitle>
                    <i className="fas fa-chart-line"></i>
                    Vendas por Período
                  </ChartTitle>
                  <div style={{ height: '300px' }}>
                    <Line data={salesChartData} options={salesChartOptions} />
                  </div>
                </ChartCard>
              )}
              {productsChartData && (
                <ChartCard>
                  <ChartTitle>
                    <i className="fas fa-chart-bar"></i>
                    Top 5 Produtos Mais Vendidos
                  </ChartTitle>
                  <div style={{ 
                    height: window.innerWidth < 768 ? '250px' : '300px',
                    width: '100%',
                    position: 'relative'
                  }}>
                    <Bar data={productsChartData} options={productsChartOptions} />
                  </div>
                </ChartCard>
              )}
            </ChartsGrid>

            {/* Relatório Financeiro */}
            {salesReport && (
              <ChartCard style={{ marginBottom: '32px' }}>
                <ChartTitle>
                  <i className="fas fa-money-bill-wave"></i>
                  Resumo Financeiro
                </ChartTitle>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#f29205', marginBottom: '8px' }}>
                      {salesReport.totalSales || 0}
                    </div>
                    <div style={{ fontSize: '14px', color: '#b0b0b0' }}>Total de Vendas</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#f29205', marginBottom: '8px' }}>
                      {formatCurrency(salesReport.totalSubtotal || 0)}
                    </div>
                    <div style={{ fontSize: '14px', color: '#b0b0b0' }}>Subtotal</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#ff4444', marginBottom: '8px' }}>
                      {formatCurrency(salesReport.totalDiscount || 0)}
                    </div>
                    <div style={{ fontSize: '14px', color: '#b0b0b0' }}>Descontos</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#4caf50', marginBottom: '8px' }}>
                      {formatCurrency(salesReport.totalRevenue || 0)}
                    </div>
                    <div style={{ fontSize: '14px', color: '#b0b0b0' }}>Receita Líquida</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#f29205', marginBottom: '8px' }}>
                      {formatCurrency(salesReport.averageTicket || 0)}
                    </div>
                    <div style={{ fontSize: '14px', color: '#b0b0b0' }}>Ticket Médio</div>
                  </div>
                </div>
              </ChartCard>
            )}

            {/* Tabela de Produtos Mais Vendidos */}
            <TableCard>
              <TableTitle>
                <i className="fas fa-trophy"></i>
                Produtos Mais Vendidos
              </TableTitle>
              {topProducts.length > 0 ? (
                <StyledTableContainer>
                  <ProductsTable>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Produto</th>
                        <th style={{ textAlign: 'right' }}>Quantidade</th>
                        <th style={{ textAlign: 'right' }}>Receita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topProducts.map((product, index) => (
                        <tr key={product.id}>
                          <td style={{ color: '#f29205', fontWeight: '600' }}>{index + 1}</td>
                          <td>{product.name}</td>
                          <td style={{ textAlign: 'right' }}>{product.totalQuantity}</td>
                          <td style={{ textAlign: 'right', color: '#4caf50', fontWeight: '600' }}>
                            {formatCurrency(product.totalRevenue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </ProductsTable>
                </StyledTableContainer>
              ) : (
                <EmptyState>
                  <i className="fas fa-box-open"></i>
                  <p>Nenhum produto vendido no período</p>
                  <span>Realize vendas para visualizar os produtos mais vendidos</span>
                </EmptyState>
              )}
            </TableCard>
          </>
        )}
      </ContentArea>
    </DashboardWrapper>
  )
}
